// Libraries
import { HamburgerIcon } from '@chakra-ui/icons';
import { Container, Flex, IconButton, Link } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import NextLink from 'next/link';
import { Fragment } from 'react';
import { useRouter } from 'next/router';
// Components
import { BearingLogo, LogoType } from '@atoms/index';
import { Breadcrumb } from '@organisms/index';
// Translations
import text from '@translations/en.json';
// Constants
import { AppPath, NavigationAuthorization } from '@constants/index';
// Types
import { Status } from '@globalTypes/index';
import { LayoutHeaderProps } from './LayoutHeader.type';
import { useAuthorization } from '@hooks/index';

/**
 * A header component for the layout that displays a hamburger icon to open the menu.
 *
 * @component
 * @param {LayoutHeaderProps} props - The props for the LayoutHeader component.
 * @param {Status} props.status - The login status of the user.
 * @param {Function} props.onOpen - The event handler for opening the menu.
 * @returns {ReactElement} The LayoutHeader component.
 */
export const LayoutHeader = ({ onOpen, status, ...rest }: LayoutHeaderProps) => {
  const router = useRouter();
  const authenticated = status === Status.authenticated;
  const logoMarginLeft = authenticated ? 0 : 8;

  const { isAuthorizationsLoading: isLoading } = useAuthorization(NavigationAuthorization);

  const loadingAnimation = keyframes`
    0%, 100% { color: #1ecbd4; }
    50% { color: #d3d3d3; }
  `;
  const animation = isLoading ? `${loadingAnimation} 1s ease-in-out infinite !important` : '';
  const cursor = isLoading ? 'not-allowed' : 'pointer';
  const pointerEvents = isLoading ? 'none' : 'auto';

  return (
    <Flex
      as='header'
      height={{ base: 16, md: 12 }}
      w='full'
      bg='navbar'
      justifyContent='flex-start'
      {...rest}
      data-testid='header'
    >
      <Container px={{ base: 3, md: 6 }} maxW={384}>
        <Flex px={0} align='center' justify='space-between' h='full' cursor={cursor}>
          {authenticated && !router.pathname.split('/').includes('auth') && (
            <Flex gap={1}>
              <IconButton
                w={8}
                h={8}
                minW={8}
                onClick={onOpen}
                aria-label={text.open_menu}
                colorScheme='navScheme'
                border={1}
                pointerEvents={pointerEvents}
                icon={
                  <HamburgerIcon
                    w={6}
                    h={6}
                    color='white'
                    __css={{
                      animation,
                    }}
                  />
                }
                data-testid='menu-button'
              />
            </Flex>
          )}
          <NextLink href={AppPath.HOME} passHref legacyBehavior>
            <Link
              display='block'
              variant='home'
              ml={logoMarginLeft}
              mr='auto'
              pointerEvents={pointerEvents}
              data-testid='bearing-logo-link'
            >
              <Fragment>
                <BearingLogo
                  w={{ base: 16, md: 24 }}
                  h={8}
                  mx={2}
                  logoType={LogoType.short}
                  display={{ base: 'inline-block', md: 'none' }}
                />
                <BearingLogo
                  w={{ base: 16, md: 24 }}
                  h={8}
                  mx={2}
                  logoType={LogoType.base}
                  display={{ base: 'none', md: 'inline-block' }}
                />
              </Fragment>
            </Link>
          </NextLink>
          <Breadcrumb
            display={{ base: 'none', lg: 'flex' }}
            borderLeft='solid 1px transparent'
            borderColor='darkBase'
            flex={1}
            ml={2}
          />
        </Flex>
      </Container>
    </Flex>
  );
};
